import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollArea } from 'components/elements';
import {
  SelectIcon,
  SelectInput,
  SelectInputText,
  SelectOptionItem,
  SelectOptionList,
  SelectOptionListItems,
  SelectPlaceholder,
  StyledSelect,
} from './Select.styled';
import { SelectOption, SelectProps } from './types';

export const Select = <TValue extends string | number>(props: SelectProps<TValue>) => {
  const { disabled = false, options = [], placeholder, value, onChange } = props;

  // Option list state
  const [expanded, setExpanded] = useState(false);

  // References to both, input and list elements
  const inputRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Currently selected option (based on the component value)
  const selectedOption = useMemo(() => options.find(o => o.value === value), [options, value]);

  /**
   * Handle clicking outside the input and list elements
   *
   * @param {MouseEvent} event Mouse event
   */
  const handleCloseOnDocumentClick = useCallback((event: MouseEvent) => {
    const isInputNode = inputRef.current ? inputRef.current.contains(event.target as Node) : false;
    const isListNode = listRef.current ? listRef.current.contains(event.target as Node) : false;

    if (!isInputNode && !isListNode) {
      setExpanded(false);
    }
  }, []);

  /**
   * Handle pressing keyboard keys
   *
   * @param {KeyboardEvent} event Mouse event
   */
  const handleCloseOnEscape = useCallback((event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      setExpanded(false);
    }
  }, []);

  /**
   * Handle clicking on the input element
   */
  const handleInputClick = useCallback(() => {
    if (disabled || !options.length) {
      return;
    }

    // Toggle expansion state
    setExpanded(!expanded);
  }, [disabled, expanded, options.length]);

  /**
   * Handle selecting an option in the list
   *
   * @param {SelectOption<TValue>} option Selected option
   */
  const handleOptionSelect = useCallback(
    (option: SelectOption<TValue>) => {
      // Only trigger the change event if the selected value is different from the current one
      if (option.value !== value) {
        onChange && onChange(option.value);
      }

      setExpanded(false);
    },
    [value, onChange],
  );

  /**
   * Render individual option items
   *
   * @param {SelectOption<TValue>} option Option properties
   * @param {number} index Option index
   */
  const renderOptionItem = useCallback(
    (option: SelectOption<TValue>, index: number) => (
      <SelectOptionItem
        key={option.value ?? `idx:${index}`}
        onClick={() => handleOptionSelect(option)}
      >
        {option.content || '\u200b'}
      </SelectOptionItem>
    ),
    [handleOptionSelect],
  );

  /**
   * Render option list
   */
  const renderOptionList = useCallback(
    () => (
      <SelectOptionList ref={listRef}>
        <ScrollArea>
          <SelectOptionListItems>{options.map(renderOptionItem)}</SelectOptionListItems>
        </ScrollArea>
      </SelectOptionList>
    ),
    [options, renderOptionItem],
  );

  // Attach and detach global event listeners
  useEffect(() => {
    document.body.addEventListener('click', handleCloseOnDocumentClick);
    document.body.addEventListener('keydown', handleCloseOnEscape);

    return () => {
      document.body.removeEventListener('click', handleCloseOnDocumentClick);
      document.body.removeEventListener('keydown', handleCloseOnEscape);
    };
  }, [handleCloseOnDocumentClick, handleCloseOnEscape]);

  return (
    <StyledSelect $disabled={disabled} $expanded={expanded} tabIndex={0}>
      <SelectInput
        $disabled={disabled}
        $expanded={expanded}
        ref={inputRef}
        onClick={handleInputClick}
      >
        {selectedOption ? (
          <SelectInputText>{selectedOption.content || '\u200b'}</SelectInputText>
        ) : (
          <SelectPlaceholder>{placeholder || '\u200b'}</SelectPlaceholder>
        )}

        <SelectIcon name="caret down" />
      </SelectInput>
      {expanded && renderOptionList()}
    </StyledSelect>
  );
};
